/**
 * Submit and delete handlers for the Products form. Reads current state from store when invoked.
 * Keeps useProductsForm focused on queries, sync effects, and UI state.
 */
import { useAppStore } from '../../../store/appStore';
import { createProduct, updateProduct, deleteProduct } from '../../../services/productService';
import {
  createProductAdditionalDetail,
  deleteProductAdditionalDetail
} from '../../../services/productAdditionalDetailService';
import { uploadProductPicture, deleteProductPicture } from '../../../services/productPicturesService';
import {
  createProductSupplier,
  updateProductSupplier,
  deleteProductSupplier
} from '../../../services/productSuppliersService';
import { clearFormAlerts } from '../../common';
import { PRODUCTS_QUERY_KEY, emptyFormData } from './productFormUtils';

/**
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {string | null} token
 * @param {ReturnType<import('./productsFormSetters').createProductsFormSetters>} setters
 * @returns {{ handleSubmit: (e: React.FormEvent) => Promise<void>, handleDelete: () => Promise<void> }}
 */
export function useProductFormSubmit(queryClient, token, setters) {
  const handleSubmit = async (e) => {
    e.preventDefault();
    const state = useAppStore.getState().productsForm;
    const {
      mode,
      formData,
      additionalDetails,
      productSuppliers,
      selectedProduct,
      pendingPictureAdds,
      pendingPictureDeletes,
      pendingAdditionalDetailDeletes,
      pendingProductSupplierDeletes
    } = state;

    const {
      setFormError,
      setFormSuccess,
      setSubmitting,
      setFormData,
      setAdditionalDetails,
      setPendingPictureAdds,
      setPendingPictureDeletes,
      setProductSuppliers,
      setPendingProductSupplierDeletes,
      setSelectedProduct,
      setProductSearchQuery,
      setPendingAdditionalDetailDeletes
    } = setters;

    clearFormAlerts(setFormError, setFormSuccess);
    setSubmitting(true);

    try {
      const productData = {
        name: formData.name?.trim() || null,
        brand: formData.brand?.trim() || null,
        product_model: formData.product_model?.trim() || null,
        description: formData.description?.trim() || null,
        sku: formData.sku?.trim() || null,
        NCM: formData.NCM?.trim() || null,
        product_universal_code: formData.product_universal_code?.trim() || null,
        category_id: formData.category_id ?? null,
        sub_category_id: formData.sub_category_id ?? null,
        condition: formData.condition?.trim() || null,
        warranty: formData.warranty?.trim() || null
      };

      if (mode === 'create') {
        if (!productData.name) {
          setFormError('Product name is required');
          setSubmitting(false);
          return;
        }
        const result = await createProduct(token, productData);
        if (result.success) {
          const productId = result.product?.id;
          if (productId != null && additionalDetails.length > 0) {
            for (const item of additionalDetails) {
              await createProductAdditionalDetail(token, {
                product_id: productId,
                detail_group: item.detail_group?.trim() || null,
                detail_name: item.detail_name?.trim() || null,
                detail_value: item.detail_value?.trim() || null,
                mlb_attribute_id: item.attribute_id ?? item.mlb_attribute_id ?? null
              });
            }
          }
          if (productId != null && pendingPictureAdds.length > 0) {
            for (const item of pendingPictureAdds) {
              await uploadProductPicture(token, productId, item.file);
            }
          }
          for (const row of productSuppliers) {
            if (row.supplier_id != null) {
              await createProductSupplier(token, {
                product_id: productId,
                supplier_id: row.supplier_id,
                origin: row.origin?.trim() || null,
                unit_price: row.unit_price != null ? Number(row.unit_price) : null,
                moq: row.moq != null ? parseInt(row.moq, 10) : null,
                stored_location: row.stored_location?.trim() || null,
                available_amount: row.available_amount != null ? Number(row.available_amount) : null,
                default_sale_price: row.default_sale_price != null ? Number(row.default_sale_price) : null,
                sale_price_currency: row.sale_price_currency?.trim() || null,
                unit_price_currency: row.unit_price_currency?.trim() || null,
                shared_stock: row.shared_stock === true,
                active: row.active !== false
              });
            }
          }
          setFormSuccess('Product created successfully!');
          setFormData(emptyFormData());
          setAdditionalDetails([]);
          setPendingPictureAdds([]);
          setPendingPictureDeletes([]);
          setProductSuppliers([]);
          setPendingProductSupplierDeletes([]);
          queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
        } else {
          setFormError(result.error || 'Failed to create product');
        }
      } else {
        if (!selectedProduct?.id) {
          setFormError('Please select a product to edit');
          setSubmitting(false);
          return;
        }
        if (!productData.name) {
          setFormError('Product name is required');
          setSubmitting(false);
          return;
        }
        const result = await updateProduct(token, selectedProduct.id, productData);
        if (result.success) {
          const newDetails = additionalDetails.filter((d) => d.id == null);
          if (newDetails.length > 0) {
            for (const item of newDetails) {
              await createProductAdditionalDetail(token, {
                product_id: selectedProduct.id,
                detail_group: item.detail_group?.trim() || null,
                detail_name: item.detail_name?.trim() || null,
                detail_value: item.detail_value?.trim() || null,
                mlb_attribute_id: item.attribute_id ?? item.mlb_attribute_id ?? null
              });
            }
          }
          for (const detailId of pendingAdditionalDetailDeletes) {
            await deleteProductAdditionalDetail(token, detailId);
          }
          if (newDetails.length > 0 || pendingAdditionalDetailDeletes.length > 0) {
            queryClient.invalidateQueries({
              queryKey: ['product-additional-details', selectedProduct.id]
            });
          }
          for (const pictureId of pendingPictureDeletes) {
            await deleteProductPicture(token, pictureId);
          }
          if (pendingPictureAdds.length > 0) {
            for (const item of pendingPictureAdds) {
              await uploadProductPicture(token, selectedProduct.id, item.file);
            }
          }
          setPendingPictureAdds([]);
          setPendingPictureDeletes([]);
          if (pendingPictureDeletes.length > 0 || pendingPictureAdds.length > 0) {
            queryClient.invalidateQueries({ queryKey: ['product-pictures', selectedProduct.id] });
          }
          for (const id of pendingProductSupplierDeletes) {
            await deleteProductSupplier(token, id);
          }
          const existingProductSupplierRows = productSuppliers.filter((r) => r.id != null);
          for (const row of existingProductSupplierRows) {
            await updateProductSupplier(token, row.id, {
              origin: row.origin?.trim() || null,
              unit_price: row.unit_price != null ? Number(row.unit_price) : null,
              moq: row.moq != null ? parseInt(row.moq, 10) : null,
              stored_location: row.stored_location?.trim() || null,
              available_amount: row.available_amount != null ? Number(row.available_amount) : null,
              default_sale_price: row.default_sale_price != null ? Number(row.default_sale_price) : null,
              sale_price_currency: row.sale_price_currency?.trim() || null,
              unit_price_currency: row.unit_price_currency?.trim() || null,
              shared_stock: row.shared_stock === true,
              active: row.active !== false
            });
          }
          const newProductSupplierRows = productSuppliers.filter((r) => r.id == null);
          for (const row of newProductSupplierRows) {
            if (row.supplier_id != null) {
              await createProductSupplier(token, {
                product_id: selectedProduct.id,
                supplier_id: row.supplier_id,
                origin: row.origin?.trim() || null,
                unit_price: row.unit_price != null ? Number(row.unit_price) : null,
                moq: row.moq != null ? parseInt(row.moq, 10) : null,
                stored_location: row.stored_location?.trim() || null,
                available_amount: row.available_amount != null ? Number(row.available_amount) : null,
                default_sale_price: row.default_sale_price != null ? Number(row.default_sale_price) : null,
                sale_price_currency: row.sale_price_currency?.trim() || null,
                unit_price_currency: row.unit_price_currency?.trim() || null,
                shared_stock: row.shared_stock === true,
                active: row.active !== false
              });
            }
          }
          if (
            pendingProductSupplierDeletes.length > 0 ||
            newProductSupplierRows.length > 0 ||
            existingProductSupplierRows.length > 0
          ) {
            queryClient.invalidateQueries({ queryKey: ['product-suppliers', selectedProduct.id] });
          }
          setFormSuccess('Changes saved successfully!');
          setFormData(emptyFormData());
          setSelectedProduct(null);
          setProductSearchQuery('');
          setAdditionalDetails([]);
          setPendingAdditionalDetailDeletes([]);
          setProductSuppliers([]);
          setPendingProductSupplierDeletes([]);
          queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
        } else {
          setFormError(result.error || 'Failed to update product');
        }
      }
    } catch (error) {
      setFormError(
        error.message || (mode === 'create' ? 'Failed to create product' : 'Failed to update product')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    const state = useAppStore.getState().productsForm;
    const { selectedProduct } = state;
    if (!selectedProduct?.id) return;

    const productPicturesResponse = queryClient.getQueryData([
      'product-pictures',
      selectedProduct.id
    ]);
    const pictures = productPicturesResponse?.product_pictures ?? [];

    const {
      setFormError,
      setFormSuccess,
      setDeleting,
      setFormData,
      setSelectedProduct,
      setProductSearchQuery,
      setShowProductDropdown
    } = setters;

    clearFormAlerts(setFormError, setFormSuccess);
    setDeleting(true);
    try {
      for (const pic of pictures) {
        if (pic.id != null) {
          await deleteProductPicture(token, pic.id);
        }
      }
      const result = await deleteProduct(token, selectedProduct.id);
      if (result.success) {
        setFormSuccess('Product deleted.');
        setFormData(emptyFormData());
        setSelectedProduct(null);
        setProductSearchQuery('');
        setShowProductDropdown(false);
        queryClient.invalidateQueries({ queryKey: PRODUCTS_QUERY_KEY });
      } else {
        setFormError(result.error || 'Failed to delete product');
      }
    } catch (error) {
      setFormError(error.message || 'Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  return { handleSubmit, handleDelete };
}
